// Reports & Analytics — v2 (rebuilt 2026-05-27)
// Three modes share the page:
//   1. Dashboard  — landing view, pre-baked charts from /api/analytics/*
//   2. Reports    — preset library + builder (hybrid model from earlier)
//   3. AI Report  — natural-language → Claude picks preset + writes a narrative

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  ChevronRight, Save, Download, FileText, Play, Trash2, BarChart3, PieChart as PieIcon,
  Loader, Sparkles, LayoutDashboard, Library, Wand2, AlertTriangle, TrendingUp,
  Ticket, Clock, Users, Building2, Activity, Send,
} from "lucide-react";
import {
  getReportPresets, getSavedReports, saveReport, deleteSavedReport, runReport, runAiReport,
  exportReportPdf, getClients, getAnalyticsOverview, getAnalyticsByClient,
  getAnalyticsCategories, getAnalyticsVolume, getAgentPerformance,
} from "../api";
import useIsMobile from "../hooks/useIsMobile";

// Trigger a PDF download from the export endpoint with the same body shape /run takes.
async function downloadReportPdf(body, suggestedName) {
  const resp = await exportReportPdf(body);
  const blob = new Blob([resp.data], { type: "application/pdf" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${(suggestedName || "report").replace(/\W+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

const C = {
  bgPage:    "var(--t-page)",
  bgPanel:   "var(--t-surface-mid)",
  bgPanelHi: "var(--t-surface-deep)",
  bdSoft:    "var(--t-border-soft)",
  bdMid:     "var(--t-border-mid)",
  txtPri:    "var(--t-text-primary)",
  txtSec:    "var(--t-text-muted)",
  txtMut:    "var(--t-text-label)",
  txtFaint:  "var(--t-text-deep)",
  blue:      "#3b82f6",
  blueSoft:  "rgba(59,130,246,0.12)",
  orange:    "#f97316",
  green:     "#22c55e",
  red:       "#ef4444",
  amber:     "#f59e0b",
};

const CHART_COLORS = ["#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#f97316", "#a78bfa"];

// ─── Charts (shared between modes) ──────────────────────────────────────────

function BarChart({ rows, xKey, yKey, label }) {
  const isMobile = useIsMobile();
  const data = rows.slice(0, 12);
  const max  = Math.max(...data.map(r => Number(r[yKey]) || 0), 1);
  return (
    <div>
      {label && <div style={{ fontSize: 12, fontWeight: 700, color: C.txtMut, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.map((r, i) => {
          const val = Number(r[yKey]) || 0;
          const pct = (val / max) * 100;
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: isMobile ? 90 : 160, fontSize: 12, color: C.txtSec, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flexShrink: 0 }} title={r[xKey]}>{r[xKey] ?? "—"}</div>
              <div style={{ flex: 1, height: 22, background: C.bgPanelHi, borderRadius: 4, overflow: "hidden", position: "relative" }}>
                <div style={{ height: "100%", width: `${pct}%`, background: C.blue, borderRadius: 4, transition: "width 0.4s ease" }} />
                <span style={{ position: "absolute", right: 8, top: 0, lineHeight: "22px", fontSize: 11, fontWeight: 700, color: pct > 35 ? "#fff" : C.txtSec }}>{val.toLocaleString()}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PieChart({ rows, xKey, yKey, label }) {
  const slices = rows.slice(0, 8).map((r, i) => ({ name: r[xKey] ?? "—", val: Number(r[yKey]) || 0, color: CHART_COLORS[i % CHART_COLORS.length] }));
  const total  = slices.reduce((s, x) => s + x.val, 0) || 1;
  let acc = 0;
  const arcs = slices.map(s => {
    const startA = (acc / total) * 2 * Math.PI - Math.PI / 2;
    acc += s.val;
    const endA = (acc / total) * 2 * Math.PI - Math.PI / 2;
    const x1 = 70 + 60 * Math.cos(startA), y1 = 70 + 60 * Math.sin(startA);
    const x2 = 70 + 60 * Math.cos(endA),   y2 = 70 + 60 * Math.sin(endA);
    const largeArc = (endA - startA) > Math.PI ? 1 : 0;
    return { ...s, d: `M70,70 L${x1},${y1} A60,60 0 ${largeArc} 1 ${x2},${y2} Z` };
  });
  return (
    <div>
      {label && <div style={{ fontSize: 12, fontWeight: 700, color: C.txtMut, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>}
      <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
        <svg width={140} height={140}>
          {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} stroke={C.bgPanel} strokeWidth="2" />)}
          <circle cx={70} cy={70} r={26} fill={C.bgPanel} />
          <text x={70} y={75} textAnchor="middle" fontWeight={700} fontSize={13} fill={C.txtPri}>{total.toLocaleString()}</text>
        </svg>
        <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 6 }}>
          {slices.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
              <span style={{ color: C.txtSec, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
              <span style={{ color: C.txtPri, fontWeight: 600 }}>{s.val.toLocaleString()}</span>
              <span style={{ color: C.txtFaint }}>{Math.round((s.val / total) * 100)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VolumeLine({ rows, label }) {
  // rows: [{date, count}] from /api/analytics/volume
  const data = (rows || []).map(r => ({ x: new Date(r.date), y: Number(r.count) || 0 })).sort((a, b) => a.x - b.x);
  if (!data.length) return <div style={{ padding: 30, textAlign: "center", color: C.txtFaint, fontSize: 12 }}>No ticket activity in the period.</div>;
  const max = Math.max(...data.map(d => d.y), 1);
  const w = 600, h = 140, padL = 30, padB = 20, padT = 10, padR = 10;
  const xStep = (w - padL - padR) / Math.max(data.length - 1, 1);
  const points = data.map((d, i) => `${padL + i * xStep},${padT + (h - padT - padB) * (1 - d.y / max)}`).join(" ");
  return (
    <div>
      {label && <div style={{ fontSize: 12, fontWeight: 700, color: C.txtMut, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>}
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ overflow: "visible" }}>
        <polyline points={points} fill="none" stroke={C.blue} strokeWidth={2} />
        {data.map((d, i) => (
          <circle key={i} cx={padL + i * xStep} cy={padT + (h - padT - padB) * (1 - d.y / max)} r={3} fill={C.blue} />
        ))}
        <line x1={padL} y1={h - padB} x2={w - padR} y2={h - padB} stroke={C.bdSoft} />
        <text x={padL - 4} y={padT + 4} textAnchor="end" fontSize={10} fill={C.txtFaint}>{max}</text>
        <text x={padL - 4} y={h - padB} textAnchor="end" fontSize={10} fill={C.txtFaint}>0</text>
        <text x={padL} y={h - 4} fontSize={9} fill={C.txtFaint}>{data[0]?.x.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</text>
        <text x={w - padR} y={h - 4} textAnchor="end" fontSize={9} fill={C.txtFaint}>{data[data.length - 1]?.x.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</text>
      </svg>
    </div>
  );
}

// ─── Sortable results table (shared) ────────────────────────────────────────

function ResultsTable({ rows, columns, availableColumns }) {
  const colDefMap = useMemo(() => Object.fromEntries((availableColumns || []).map(c => [c.key, c])), [availableColumns]);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const an = Number(av), bn = Number(bv);
      if (!isNaN(an) && !isNaN(bn)) return (an - bn) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sortKey, sortDir]);
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  if (!rows?.length) return <div style={{ padding: 36, textAlign: "center", color: C.txtFaint, fontSize: 13 }}>No data.</div>;
  return (
    <div style={{ overflowX: "auto", border: `1px solid ${C.bdSoft}`, borderRadius: 8 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: C.bgPanelHi }}>
            {columns.map(key => (
              <th key={key} onClick={() => toggleSort(key)} style={{
                textAlign: "left", padding: "10px 12px", color: C.txtMut,
                fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
                borderBottom: `1px solid ${C.bdSoft}`,
              }}>
                {colDefMap[key]?.label || key}
                {sortKey === key && <span style={{ marginLeft: 4, color: C.blue }}>{sortDir === "asc" ? "▲" : "▼"}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${C.bdSoft}` }}>
              {columns.map(key => {
                const v = row[key];
                const display = v == null ? "—"
                  : typeof v === "number" ? v.toLocaleString()
                  : (/^\d{4}-\d{2}-\d{2}T/.test(String(v)) ? new Date(v).toLocaleString() : String(v));
                return <td key={key} style={{ padding: "8px 12px", color: C.txtSec }}>{display}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── DASHBOARD MODE ─────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color = C.blue }) {
  return (
    <div style={{ background: C.bgPanel, border: `1px solid ${C.bdSoft}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Icon size={14} color={color} />
        <span style={{ fontSize: 10, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: C.txtPri, letterSpacing: "-0.5px" }}>{value ?? "—"}</div>
      {sub && <div style={{ fontSize: 11, color: C.txtFaint, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: C.bgPanel, border: `1px solid ${C.bdSoft}`, borderRadius: 10, padding: 18 }}>
      {title && <div style={{ fontSize: 13, fontWeight: 700, color: C.txtPri, marginBottom: 14 }}>{title}</div>}
      {children}
    </div>
  );
}

function Dashboard() {
  const [overview, setOverview]   = useState(null);
  const [byClient, setByClient]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [volume, setVolume]       = useState([]);
  const [agents, setAgents]       = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      getAnalyticsOverview(),
      getAnalyticsByClient(),
      getAnalyticsCategories(),
      getAnalyticsVolume(),
      getAgentPerformance(30),
    ]).then(([o, c, cat, v, ag]) => {
      setOverview(o.data);
      setByClient(c.data || []);
      setCategories(cat.data || []);
      setVolume(v.data || []);
      setAgents(ag.data?.agents || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ padding: 60, textAlign: "center", color: C.txtFaint }}><Loader size={20} style={{ animation: "spin 1s linear infinite" }} /></div>;
  }

  const t  = overview?.tickets || {};
  const open = (Number(t.open) || 0) + (Number(t.in_progress) || 0);
  const slaBreaches = Number(t.sla_breaches) || 0;
  const avgResp = Number(t.avg_first_response_mins) || 0;
  const avgRespDisplay = avgResp >= 60 ? `${(avgResp / 60).toFixed(1)}h` : `${avgResp}m`;
  const onlineAgents = agents.filter(a => Number(a.resolved_tickets || a.responses_sent) > 0).length;

  return (
    <div>
      {/* Stat cards row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <StatCard icon={Ticket}   label="Open Tickets"   value={open.toLocaleString()} sub={`${t.open || 0} open · ${t.in_progress || 0} in progress`} color={C.blue} />
        <StatCard icon={AlertTriangle} label="SLA Breaches" value={slaBreaches} sub={slaBreaches === 0 ? "All within SLA" : "Needs attention"} color={slaBreaches > 0 ? C.red : C.green} />
        <StatCard icon={Clock}    label="Avg First Response" value={avgRespDisplay} sub="last 30 days" color={C.amber} />
        <StatCard icon={TrendingUp} label="Resolved (30d)" value={(t.resolved || 0).toLocaleString()} color={C.green} />
        <StatCard icon={Users}    label="Active Agents"  value={onlineAgents}  sub={`${agents.length} total · last 30d`} color={C.blue} />
      </div>

      {/* Chart grid */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 14 }}>
        <Card title="Ticket Volume — Last 30 Days">
          <VolumeLine rows={volume} />
        </Card>
        <Card title="Tickets by Category">
          <PieChart rows={categories} xKey="category" yKey="count" />
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Card title="Top Clients by Ticket Count">
          <BarChart rows={byClient} xKey="company_name" yKey="total_tickets" />
        </Card>
        <Card title="Agent Workload (last 30d)">
          <BarChart
            rows={agents.map(a => ({ name: (a.agent_name || "").trim() || "—", resolved: Number(a.resolved_tickets) || 0 }))}
            xKey="name" yKey="resolved" />
        </Card>
      </div>
    </div>
  );
}

// ─── REPORTS MODE (preset library + builder) ─────────────────────────────────

function ReportsBuilder() {
  const isMobile = useIsMobile();
  const [presets,       setPresets]       = useState([]);
  const [savedReports,  setSavedReports]  = useState([]);
  const [clients,       setClients]       = useState([]);
  const [selectedId,    setSelectedId]    = useState(null);
  const [filters,       setFilters]       = useState({});
  const [columns,       setColumns]       = useState([]);
  const [chartType,     setChartType]     = useState("bar");
  const [running,       setRunning]       = useState(false);
  const [result,        setResult]        = useState(null);
  const [error,         setError]         = useState(null);
  const [showSave,      setShowSave]      = useState(false);
  const [saveName,      setSaveName]      = useState("");

  useEffect(() => {
    Promise.all([getReportPresets(), getSavedReports(), getClients()])
      .then(([p, s, c]) => {
        setPresets(p.data || []);
        setSavedReports(s.data || []);
        setClients(c.data || []);
        if (p.data?.length && !selectedId) selectPreset(p.data[0]);
      })
      .catch(e => setError(e.response?.data?.error || e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(() => {
    if (!selectedId) return null;
    if (selectedId.startsWith("saved:")) {
      const id = selectedId.slice(6);
      const sr = savedReports.find(s => s.id === id);
      if (!sr) return null;
      const preset = presets.find(p => p.id === sr.preset_id);
      return { kind: "saved", saved: sr, preset };
    }
    return { kind: "preset", preset: presets.find(p => p.id === selectedId) };
  }, [selectedId, presets, savedReports]);

  const selectPreset = useCallback((p) => {
    setSelectedId(p.id);
    setFilters(p.default_filters || {});
    setColumns(p.default_columns || []);
    setChartType(p.default_chart?.type || "bar");
    setResult(null);
  }, []);

  const selectSaved = useCallback((s) => {
    setSelectedId(`saved:${s.id}`);
    const p = presets.find(x => x.id === s.preset_id);
    setFilters({ ...(p?.default_filters || {}), ...(s.spec?.filters || {}) });
    setColumns(s.spec?.columns || p?.default_columns || []);
    setChartType(s.spec?.chart?.type || p?.default_chart?.type || "bar");
    setResult(null);
  }, [presets]);

  const runIt = useCallback(async () => {
    if (!selected?.preset) return;
    setRunning(true); setError(null); setResult(null);
    try {
      const body = selected.kind === "saved"
        ? { saved_report_id: selected.saved.id, filters, columns }
        : { preset_id: selected.preset.id, filters, columns };
      const r = await runReport(body);
      setResult(r.data);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setRunning(false); }
  }, [selected, filters, columns]);

  const handleSave = useCallback(async () => {
    if (!saveName.trim() || !selected?.preset) return;
    try {
      const r = await saveReport({
        name: saveName.trim(),
        preset_id: selected.preset.id,
        spec: { filters, columns, chart: { ...(selected.preset.default_chart || {}), type: chartType } },
      });
      setSavedReports(prev => [r.data, ...prev]);
      setShowSave(false); setSaveName("");
      setSelectedId(`saved:${r.data.id}`);
    } catch (e) { setError(e.response?.data?.error || e.message); }
  }, [saveName, selected, filters, columns, chartType]);

  const handleDeleteSaved = useCallback(async (id) => {
    if (!window.confirm("Delete this saved report?")) return;
    try {
      await deleteSavedReport(id);
      setSavedReports(prev => prev.filter(s => s.id !== id));
      if (selectedId === `saved:${id}`) { const first = presets[0]; if (first) selectPreset(first); }
    } catch (e) { setError(e.response?.data?.error || e.message); }
  }, [selectedId, presets, selectPreset]);

  const exportCsv = useCallback(() => {
    if (!result) return;
    const labels = Object.fromEntries((result.available_columns || []).map(c => [c.key, c.label]));
    const head = columns.map(k => `"${(labels[k] || k).replace(/"/g, '""')}"`).join(",");
    const body = (result.rows || []).map(r => columns.map(k => {
      const v = r[k];
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(selected?.preset?.name || "report").replace(/\W+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [result, columns, selected]);

  const toggleColumn = (key) => setColumns(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const presetsByCategory = useMemo(() => {
    const groups = {};
    for (const p of presets) (groups[p.category] = groups[p.category] || []).push(p);
    return groups;
  }, [presets]);

  return (
    <>
      {error && <div style={{ background: "rgba(239,68,68,0.08)", border: `1px solid ${C.red}44`, color: C.red, fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 14 }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "240px 1fr", gap: 16 }}>
        <div style={{ background: C.bgPanel, border: `1px solid ${C.bdSoft}`, borderRadius: 10, padding: 14, height: "fit-content" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Presets</div>
          {Object.entries(presetsByCategory).map(([cat, ps]) => (
            <div key={cat} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.txtFaint, marginBottom: 6, textTransform: "capitalize" }}>{cat}</div>
              {ps.map(p => (
                <button key={p.id} onClick={() => selectPreset(p)} style={{
                  display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left",
                  padding: "8px 10px", marginBottom: 4, fontSize: 13,
                  background: selectedId === p.id ? C.blueSoft : "transparent",
                  border: `1px solid ${selectedId === p.id ? C.blue + "44" : "transparent"}`,
                  borderRadius: 6, cursor: "pointer",
                  color: selectedId === p.id ? C.blue : C.txtSec, fontFamily: "inherit",
                }}>
                  <ChevronRight size={12} style={{ flexShrink: 0, opacity: selectedId === p.id ? 1 : 0.5 }} />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                </button>
              ))}
            </div>
          ))}
          {savedReports.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10, marginTop: 18 }}>Saved Reports</div>
              {savedReports.map(s => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                  <button onClick={() => selectSaved(s)} style={{
                    flex: 1, display: "flex", alignItems: "center", gap: 6, textAlign: "left",
                    padding: "8px 10px", fontSize: 13,
                    background: selectedId === `saved:${s.id}` ? C.blueSoft : "transparent",
                    border: `1px solid ${selectedId === `saved:${s.id}` ? C.blue + "44" : "transparent"}`,
                    borderRadius: 6, cursor: "pointer",
                    color: selectedId === `saved:${s.id}` ? C.blue : C.txtSec, fontFamily: "inherit",
                  }}>
                    <Sparkles size={12} style={{ flexShrink: 0, opacity: 0.6 }} />
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</span>
                  </button>
                  <button onClick={() => handleDeleteSaved(s.id)} title="Delete" style={{ background: "none", border: "none", color: C.txtFaint, padding: "4px 6px", cursor: "pointer" }}><Trash2 size={12} /></button>
                </div>
              ))}
            </>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {selected?.preset && (
            <div style={{ background: C.bgPanel, border: `1px solid ${C.bdSoft}`, borderRadius: 10, padding: 18 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14, gap: 12, flexWrap: isMobile ? "wrap" : undefined }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.txtPri }}>{selected.kind === "saved" ? selected.saved.name : selected.preset.name}</div>
                  <div style={{ fontSize: 12, color: C.txtFaint, marginTop: 3 }}>{selected.preset.description}</div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: isMobile ? "wrap" : undefined }}>
                  <button onClick={runIt} disabled={running}
                    style={{ background: C.blue, color: "#fff", border: 0, padding: "8px 18px", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: running ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6, opacity: running ? 0.7 : 1 }}>
                    {running ? <Loader size={13} style={{ animation: "spin 1s linear infinite" }} /> : <Play size={13} />}
                    {running ? "Running…" : "Run"}
                  </button>
                  <button onClick={() => setShowSave(true)} disabled={!result}
                    style={{ background: "none", color: C.txtSec, border: `1px solid ${C.bdMid}`, padding: "8px 14px", borderRadius: 7, fontSize: 13, cursor: result ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6, opacity: result ? 1 : 0.5 }}>
                    <Save size={13} /> Save as…
                  </button>
                  <button onClick={exportCsv} disabled={!result}
                    style={{ background: "none", color: C.txtSec, border: `1px solid ${C.bdMid}`, padding: "8px 14px", borderRadius: 7, fontSize: 13, cursor: result ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6, opacity: result ? 1 : 0.5 }}>
                    <Download size={13} /> CSV
                  </button>
                  <button
                    onClick={() => {
                      if (!selected?.preset || !result) return;
                      const body = selected.kind === "saved"
                        ? { saved_report_id: selected.saved.id, filters, columns }
                        : { preset_id: selected.preset.id, filters, columns };
                      downloadReportPdf(body, selected.kind === "saved" ? selected.saved.name : selected.preset.name)
                        .catch(e => setError(e.response?.data?.error || e.message));
                    }}
                    disabled={!result}
                    style={{ background: "none", color: C.txtSec, border: `1px solid ${C.bdMid}`, padding: "8px 14px", borderRadius: 7, fontSize: 13, cursor: result ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6, opacity: result ? 1 : 0.5 }}>
                    <FileText size={13} /> PDF
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                {(selected.preset.available_filters || []).map(name => (
                  <FilterRow key={name} name={name} value={filters[name]} clients={clients}
                    onChange={v => setFilters(prev => ({ ...prev, [name]: v }))} />
                ))}
              </div>

              <div style={{ borderTop: `1px solid ${C.bdSoft}`, paddingTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Columns</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(selected.preset.available_columns || []).map(col => {
                    const on = columns.includes(col.key);
                    return (
                      <button key={col.key} onClick={() => toggleColumn(col.key)}
                        style={{
                          padding: "4px 10px", fontSize: 12,
                          background: on ? C.blueSoft : "transparent",
                          border: `1px solid ${on ? C.blue + "55" : C.bdMid}`,
                          color: on ? C.blue : C.txtSec,
                          borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
                        }}>{col.label}</button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.06em", marginRight: 6 }}>Chart</span>
                {[
                  { v: "bar", icon: BarChart3, label: "Bar" },
                  { v: "pie", icon: PieIcon,   label: "Pie" },
                  { v: "none", icon: null,     label: "None" },
                ].map(opt => (
                  <button key={opt.v} onClick={() => setChartType(opt.v)}
                    style={{
                      padding: "4px 10px", fontSize: 12,
                      background: chartType === opt.v ? C.blueSoft : "transparent",
                      border: `1px solid ${chartType === opt.v ? C.blue + "55" : C.bdMid}`,
                      color: chartType === opt.v ? C.blue : C.txtSec,
                      borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
                      display: "flex", alignItems: "center", gap: 4,
                    }}>{opt.icon ? <opt.icon size={11} /> : null}{opt.label}</button>
                ))}
              </div>
            </div>
          )}

          {result && (
            <div style={{ background: C.bgPanel, border: `1px solid ${C.bdSoft}`, borderRadius: 10, padding: 18 }}>
              {result.summary && Object.keys(result.summary).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${C.bdSoft}` }}>
                  {Object.entries(result.summary).map(([k, v]) => (
                    <div key={k} style={{ background: C.bgPanelHi, padding: "8px 14px", borderRadius: 8, minWidth: 110 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.06em" }}>{k.replace(/_/g, " ")}</div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: C.txtPri, marginTop: 2 }}>{typeof v === "object" ? JSON.stringify(v) : (typeof v === "number" ? v.toLocaleString() : String(v))}</div>
                    </div>
                  ))}
                </div>
              )}
              {chartType !== "none" && result.chart && result.chart_data?.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  {chartType === "bar"
                    ? <BarChart rows={result.chart_data} xKey={result.chart.x} yKey={result.chart.y} label={result.chart.label || result.name} />
                    : <PieChart rows={result.chart_data} xKey={result.chart.x} yKey={result.chart.y} label={result.chart.label || result.name} />}
                </div>
              )}
              <ResultsTable rows={result.rows || []} columns={columns} availableColumns={result.available_columns} />
              <div style={{ marginTop: 12, fontSize: 11, color: C.txtFaint, textAlign: "right" }}>
                {result.rows?.length || 0} row{result.rows?.length !== 1 ? "s" : ""} · ran in {result.took_ms}ms
              </div>
            </div>
          )}

          {!result && !running && selected?.preset && (
            <div style={{ padding: 36, textAlign: "center", color: C.txtFaint, fontSize: 13, background: C.bgPanel, border: `1px dashed ${C.bdSoft}`, borderRadius: 10 }}>
              Click <strong style={{ color: C.txtSec }}>Run</strong> to generate the report.
            </div>
          )}
        </div>
      </div>

      {showSave && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 16 : undefined }} onClick={() => setShowSave(false)}>
          <div style={{ background: C.bgPanel, padding: 24, borderRadius: 12, width: "100%", maxWidth: 440, border: `1px solid ${C.bdMid}` }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.txtPri, marginBottom: 14 }}>Save report</div>
            <input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="Report name…"
              style={{ width: "100%", padding: "10px 14px", background: C.bgPanelHi, border: `1px solid ${C.bdMid}`, borderRadius: 7, color: C.txtPri, fontSize: 13, marginBottom: 14, boxSizing: "border-box", outline: "none" }} autoFocus />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowSave(false)} style={{ background: "none", color: C.txtSec, border: `1px solid ${C.bdMid}`, padding: "8px 16px", borderRadius: 7, fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleSave} disabled={!saveName.trim()} style={{ background: C.blue, color: "#fff", border: 0, padding: "8px 16px", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: saveName.trim() ? "pointer" : "not-allowed", opacity: saveName.trim() ? 1 : 0.5 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function FilterRow({ name, value, onChange, clients }) {
  const inputStyle = {
    padding: "7px 10px", background: C.bgPanelHi, border: `1px solid ${C.bdSoft}`,
    borderRadius: 6, color: C.txtPri, fontSize: 13, outline: "none", fontFamily: "inherit",
  };
  if (name === "days_back") return (
    <select value={value ?? 30} onChange={e => onChange(parseInt(e.target.value))} style={{ ...inputStyle, cursor: "pointer", minWidth: 140 }}>
      {[7, 14, 30, 60, 90, 180, 365].map(d => <option key={d} value={d}>Last {d} days</option>)}
    </select>
  );
  if (name === "client_id") return (
    <select value={value || ""} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, cursor: "pointer", minWidth: 180 }}>
      <option value="">All Clients</option>
      {(clients || []).map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
    </select>
  );
  if (name === "priority") return (
    <select value={value || ""} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, cursor: "pointer", minWidth: 140 }}>
      <option value="">All Priorities</option>
      {["Critical", "High", "Medium", "Low"].map(p => <option key={p} value={p}>{p}</option>)}
    </select>
  );
  if (name === "status") return (
    <select value={value || ""} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, cursor: "pointer", minWidth: 140 }}>
      <option value="">All Statuses</option>
      {["online", "offline", "warning", "critical", "unknown"].map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  );
  if (name === "group_by") return (
    <select value={value || "client"} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, cursor: "pointer", minWidth: 160 }}>
      <option value="client">Group by Client</option>
      <option value="priority">Group by Priority</option>
    </select>
  );
  return null;
}

// ─── SAVED REPORTS TAB ───────────────────────────────────────────────────────
// Dedicated home for "my" reports. Server already restricts by owner, so this
// reflects only the current user's reports (per-user isolation enforced server-side).

function SavedReportsTab() {
  const [reports, setReports] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);  // id of currently-running report
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  useEffect(() => {
    Promise.all([getSavedReports(), getReportPresets()])
      .then(([s, p]) => { setReports(s.data || []); setPresets(p.data || []); })
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  const presetById = useMemo(() => Object.fromEntries(presets.map(p => [p.id, p])), [presets]);

  const runSaved = useCallback(async (sr) => {
    setRunning(sr.id); setError(null); setResult(null);
    try {
      const r = await runReport({ saved_report_id: sr.id });
      setResult({ ...r.data, _savedReport: sr });
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setRunning(null); }
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Delete this saved report?")) return;
    try {
      await deleteSavedReport(id);
      setReports(prev => prev.filter(r => r.id !== id));
      if (result?._savedReport?.id === id) setResult(null);
    } catch (e) { setError(e.response?.data?.error || e.message); }
  }, [result]);

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: C.txtFaint }}><Loader size={20} style={{ animation: "spin 1s linear infinite" }} /></div>;

  if (!reports.length) {
    return (
      <Card>
        <div style={{ padding: 36, textAlign: "center" }}>
          <Sparkles size={28} color={C.txtFaint} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: C.txtPri, marginBottom: 6 }}>No saved reports yet</div>
          <div style={{ fontSize: 12, color: C.txtFaint, maxWidth: 380, margin: "0 auto", lineHeight: 1.6 }}>
            Run a preset under the <strong style={{ color: C.txtSec }}>Reports</strong> tab, tune its filters and columns,
            then click <strong style={{ color: C.txtSec }}>Save as…</strong>. Your saved reports appear here.
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {error && <div style={{ background: "rgba(239,68,68,0.08)", border: `1px solid ${C.red}44`, color: C.red, fontSize: 13, padding: "10px 14px", borderRadius: 8 }}>{error}</div>}

      {/* Saved reports grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
        {reports.map(sr => {
          const p = presetById[sr.preset_id];
          const filtersSummary = Object.entries(sr.spec?.filters || {})
            .map(([k, v]) => `${k}: ${v}`).slice(0, 3).join(" · ");
          const isRunning = running === sr.id;
          return (
            <div key={sr.id} style={{ background: C.bgPanel, border: `1px solid ${C.bdSoft}`, borderRadius: 10, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.txtPri, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sr.name}</div>
                  {p && <div style={{ fontSize: 11, color: C.txtFaint, marginTop: 2 }}>from <strong>{p.name}</strong></div>}
                </div>
                <button onClick={() => handleDelete(sr.id)} title="Delete" style={{ background: "none", border: "none", color: C.txtFaint, padding: 4, cursor: "pointer" }}><Trash2 size={13} /></button>
              </div>
              {filtersSummary && <div style={{ fontSize: 11, color: C.txtSec, fontFamily: "monospace", background: C.bgPanelHi, padding: "6px 10px", borderRadius: 6 }}>{filtersSummary}</div>}
              <div style={{ fontSize: 10, color: C.txtFaint }}>Saved {new Date(sr.created_at).toLocaleDateString()}</div>
              <button onClick={() => runSaved(sr)} disabled={isRunning} style={{
                background: isRunning ? C.bgPanelHi : C.blue, color: "#fff", border: 0,
                padding: "7px 14px", borderRadius: 7, fontSize: 12, fontWeight: 700,
                cursor: isRunning ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6, justifyContent: "center",
              }}>
                {isRunning ? <><Loader size={12} style={{ animation: "spin 1s linear infinite" }} /> Running…</> : <><Play size={12} /> Run</>}
              </button>
            </div>
          );
        })}
      </div>

      {/* Result */}
      {result && (
        <Card title={result.name}>
          {result.summary && Object.keys(result.summary).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${C.bdSoft}` }}>
              {Object.entries(result.summary).map(([k, v]) => (
                <div key={k} style={{ background: C.bgPanelHi, padding: "8px 14px", borderRadius: 8, minWidth: 110 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.06em" }}>{k.replace(/_/g, " ")}</div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: C.txtPri, marginTop: 2 }}>{typeof v === "object" ? JSON.stringify(v) : (typeof v === "number" ? v.toLocaleString() : String(v))}</div>
                </div>
              ))}
            </div>
          )}
          {result.chart && result.chart_data?.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              {(result.chart.type === "pie")
                ? <PieChart rows={result.chart_data} xKey={result.chart.x} yKey={result.chart.y} label={result.chart.label || result.name} />
                : <BarChart rows={result.chart_data} xKey={result.chart.x} yKey={result.chart.y} label={result.chart.label || result.name} />}
            </div>
          )}
          <ResultsTable rows={result.rows || []} columns={result.columns} availableColumns={result.available_columns} />
          <div style={{ marginTop: 12, fontSize: 11, color: C.txtFaint, textAlign: "right" }}>
            {result.rows?.length || 0} row{result.rows?.length !== 1 ? "s" : ""} · ran in {result.took_ms}ms
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── AI REPORT MODE ─────────────────────────────────────────────────────────

function AiReport() {
  const isMobile = useIsMobile();
  const [question, setQuestion] = useState("");
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);

  const examples = [
    "Which clients had the most tickets last 60 days?",
    "How is our SLA performance by priority this month?",
    "Show me agent performance over the last 90 days",
    "Give me a breakdown of devices by status",
  ];

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setRunning(true); setError(null); setResult(null);
    try {
      const r = await runAiReport(text);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setRunning(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 980 }}>
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: "linear-gradient(135deg, #f97316, #3b82f6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Wand2 size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.txtPri }}>Ask for a report</div>
            <div style={{ fontSize: 12, color: C.txtFaint, marginTop: 2 }}>Describe what you want to know. Claude picks the right report, sets the filters, and writes a quick summary.</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: isMobile ? "wrap" : undefined }}>
          <input
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
            placeholder="e.g. Which clients had the most tickets last quarter?"
            style={{ flex: isMobile ? "1 1 100%" : 1, padding: "12px 16px", background: C.bgPanelHi, border: `1px solid ${C.bdMid}`, borderRadius: 8, color: C.txtPri, fontSize: 14, outline: "none", fontFamily: "inherit" }}
            autoFocus
          />
          <button onClick={() => ask()} disabled={running || !question.trim()}
            style={{ background: C.orange, color: "#fff", border: 0, padding: "12px 22px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: (running || !question.trim()) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7, opacity: (running || !question.trim()) ? 0.5 : 1 }}>
            {running ? <Loader size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Send size={14} />}
            {running ? "Thinking…" : "Ask"}
          </button>
        </div>

        {!result && !running && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Try one of these</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {examples.map(ex => (
                <button key={ex} onClick={() => { setQuestion(ex); ask(ex); }}
                  style={{ padding: "6px 12px", fontSize: 12, background: "transparent", border: `1px solid ${C.bdMid}`, borderRadius: 99, color: C.txtSec, cursor: "pointer", fontFamily: "inherit" }}>{ex}</button>
              ))}
            </div>
          </div>
        )}
      </Card>

      {error && <div style={{ background: "rgba(239,68,68,0.08)", border: `1px solid ${C.red}44`, color: C.red, fontSize: 13, padding: "10px 14px", borderRadius: 8 }}>{error}</div>}

      {result && (
        <>
          {/* Narrative card */}
          <Card>
            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <Sparkles size={18} color={C.orange} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>AI Summary</div>
                <div style={{ fontSize: 14, color: C.txtPri, lineHeight: 1.65 }}>{result.narrative}</div>
                <div style={{ fontSize: 11, color: C.txtFaint, marginTop: 10 }}>Generated from <strong>{result.name}</strong> preset · filters: <code style={{ color: C.txtSec, fontFamily: "monospace" }}>{JSON.stringify(result.filters)}</code></div>
              </div>
            </div>
          </Card>

          {/* Result card */}
          <Card title={result.name}>
            {result.summary && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${C.bdSoft}` }}>
                {Object.entries(result.summary).map(([k, v]) => (
                  <div key={k} style={{ background: C.bgPanelHi, padding: "8px 14px", borderRadius: 8, minWidth: 110 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: C.txtMut, textTransform: "uppercase", letterSpacing: "0.06em" }}>{k.replace(/_/g, " ")}</div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: C.txtPri, marginTop: 2 }}>{typeof v === "object" ? JSON.stringify(v) : (typeof v === "number" ? v.toLocaleString() : String(v))}</div>
                  </div>
                ))}
              </div>
            )}
            {result.chart && result.chart_data?.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <BarChart rows={result.chart_data} xKey={result.chart.x} yKey={result.chart.y} label={result.chart.label || result.name} />
              </div>
            )}
            <ResultsTable rows={result.rows || []} columns={result.columns} availableColumns={result.available_columns} />
          </Card>
        </>
      )}
    </div>
  );
}

// ─── MAIN PAGE ──────────────────────────────────────────────────────────────

const TABS = [
  { id: "dashboard", label: "Dashboard",        icon: LayoutDashboard },
  { id: "reports",   label: "Reports",          icon: Library },
  { id: "saved",     label: "Saved Reports",    icon: Sparkles },
  { id: "ai",        label: "Create AI Report", icon: Wand2 },
];

export default function Analytics() {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("dashboard");

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth: 1600 }}>
      <div style={{ paddingBottom: 16, borderBottom: `1px solid ${C.bdSoft}`, marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.txtPri, letterSpacing: "-0.5px", margin: 0 }}>Reports &amp; Analytics</h1>
          <p style={{ color: C.txtFaint, fontSize: 13, marginTop: 4, margin: 0 }}>
            {tab === "dashboard" && "At-a-glance view of helpdesk activity, agent workload, and SLA performance."}
            {tab === "reports"   && "Run preset reports, tune the filters, save your favorite configurations."}
            {tab === "ai"        && "Ask a question in plain English; Claude picks the right report and writes a summary."}
          </p>
        </div>
        <div style={{ display: "flex", gap: 4, background: C.bgPanel, padding: 4, border: `1px solid ${C.bdSoft}`, borderRadius: 10, flexWrap: isMobile ? "wrap" : undefined, maxWidth: "100%" }}>
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} style={{
              padding: isMobile ? "8px 10px" : "8px 14px", fontSize: 13, fontWeight: 600,
              background: tab === id ? C.blue : "transparent",
              color: tab === id ? "#fff" : C.txtSec,
              border: 0, borderRadius: 7, cursor: "pointer", fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 7,
            }}>
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {tab === "dashboard" && <Dashboard />}
      {tab === "reports"   && <ReportsBuilder />}
      {tab === "saved"     && <SavedReportsTab onEdit={() => setTab("reports")} />}
      {tab === "ai"        && <AiReport />}

      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
